import { Component, ChangeDetectionStrategy, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconBadgeComponent, IconBadgeVariant, IconBadgeSize } from '../icon-badge/icon-badge.component';
import { ButtonComponent } from '../button/button.component';

export interface SectionAction {
  label: string;
  icon?: string;
  variant?: 'primary' | 'secondary' | 'white' | 'gradient' | 'outline-primary';
  action: () => void;
}

/**
 * SectionHeader Component - Section title with icon badge and optional action button
 *
 * @example
 * <!-- Simple section header -->
 * <app-atom-section-header
 *   title="Projects Overview"
 *   description="Manage and track all your projects">
 * </app-atom-section-header>
 *
 * <!-- With icon and action -->
 * <app-atom-section-header
 *   title="Project Management"
 *   description="View and manage projects by status"
 *   icon="folder"
 *   iconVariant="gray"
 *   [action]="{ label: 'New Program', icon: 'ti-plus', variant: 'outline-primary', action: createProgram }">
 * </app-atom-section-header>
 */
@Component({
  selector: 'app-atom-section-header',
  standalone: true,
  imports: [CommonModule, IconBadgeComponent, ButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-3">
        @if (icon()) {
          <app-atom-icon-badge
            [icon]="icon()!"
            [variant]="iconVariant()"
            [size]="iconSize()">
          </app-atom-icon-badge>
        }
        <div>
          <h3 [class]="titleClasses()">{{ title() }}</h3>
          @if (description()) {
            <p class="text-sm text-gray-500">{{ description() }}</p>
          }
        </div>
      </div>
      
      <!-- Multiple actions (if provided) -->
      @if (actions().length > 0) {
        <div class="flex items-center gap-3">
          @for (actionItem of actions(); track actionItem.label) {
            <app-atom-button
              [variant]="actionItem.variant || 'outline-primary'"
              size="md"
              (componentClick)="actionItem.action()">
              @if (actionItem.icon) {
                <i [class]="actionItem.icon + ' mr-2'"></i>
              }
              {{ actionItem.label }}
            </app-atom-button>
          }
        </div>
      }
      
      <!-- Single action (backward compatibility) -->
      @else if (action()) {
        <app-atom-button
          [variant]="action()!.variant || 'outline-primary'"
          size="md"
          (componentClick)="action()!.action()">
          @if (action()!.icon) {
            <i [class]="action()!.icon + ' mr-2'"></i>
          }
          {{ action()!.label }}
        </app-atom-button>
      }
    </div>
  `,
  styles: [`
    :host {
      display: block;
    }
  `]
})
export class SectionHeaderComponent {
  /** Section title */
  title = input.required<string>();
  
  /** Optional description */
  description = input<string>('');
  
  /** Icon name for icon badge */
  icon = input<string | null>(null);
  
  /** Icon badge color variant */
  iconVariant = input<IconBadgeVariant>('blue');
  
  /** Icon badge size */
  iconSize = input<IconBadgeSize>('md');
  
  /** Title size */
  titleSize = input<'sm' | 'md' | 'lg'>('md');
  
  /** Action button configuration (single) */
  action = input<SectionAction | null>(null);

  /** Multiple action buttons configuration */
  actions = input<SectionAction[]>([]);

  /** Get title classes based on size */
  titleClasses(): string {
    const sizeMap = {
      'sm': 'text-base font-semibold text-gray-900',
      'md': 'text-lg font-semibold text-gray-900',
      'lg': 'text-xl font-semibold text-gray-900'
    };
    return sizeMap[this.titleSize()];
  }
}
